import { useState, useRef, useEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

export interface EntityComboboxOption {
  id: string;
  companyName: string;
}

interface EntityComboboxProps {
  entities: EntityComboboxOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /** Shown as the first row when set — selecting it calls onChange(''). Use for optional fields (e.g. "No broker"). */
  clearLabel?: string;
  /** Shown as the last row when set, alongside onCreateNew — replaces the old '__create_new__' <option> sentinel. */
  createLabel?: string;
  onCreateNew?: () => void;
  disabled?: boolean;
  style?: CSSProperties;
  /** Applied to the internal <input> itself (the outer `style` prop above
   *  only reaches the wrapping <div>) — e.g. so a parent Excel-style grid
   *  can flatten it to a borderless, edge-to-edge cell. */
  inputClassName?: string;
  /** Exposes the internal <input> DOM node — e.g. so a parent Excel-style
   *  grid can register this cell for arrow-key navigation. Composed with
   *  the component's own internal ref, not a replacement for it. */
  cellRef?: (el: HTMLInputElement | null) => void;
  /** Fires after the component's own Escape/Enter handling — e.g. so a
   *  parent grid can additionally handle ArrowUp/Down/Left/Right without
   *  taking over the input's own key handling. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

interface Position {
  top: number;
  left: number;
  width: number;
}

/**
 * Type-to-filter replacement for a plain <select> of business entities.
 * Shows the selected entity's name when unfocused; on focus becomes a
 * search box that filters `entities` by substring match, with results in
 * a portaled dropdown panel (same technique as Dropdown.tsx) so it isn't
 * clipped by a modal's own scroll container.
 */
export function EntityCombobox({
  entities, value, onChange, placeholder, clearLabel, createLabel, onCreateNew, disabled, style,
  inputClassName, cellRef, onKeyDown: onKeyDownProp,
}: EntityComboboxProps) {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<Position | null>(null);
  // Keyboard highlight over the panel's rows (clear row, matches, create row).
  const [hi, setHi] = useState(-1);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selected = entities.find(e => e.id === value);

  useEffect(() => {
    if (!focused) return;
    function updatePosition() {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPos({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [focused]);

  useEffect(() => {
    if (!focused) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setFocused(false);
      setQuery('');
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [focused]);

  const filtered = query.trim()
    ? entities.filter(e => (e.companyName ?? '').toLowerCase().includes(query.trim().toLowerCase()))
    : entities;

  // Every row in panel order, so Up / Down / Enter can walk them.
  type Row = { kind: 'clear' } | { kind: 'entity'; id: string } | { kind: 'create' };
  // "Create New Entity" sits first so it's always in view, then the clear
  // row, then the matches.
  const hasCreate = !!(createLabel && onCreateNew);
  const clearIdx = hasCreate ? 1 : 0;
  const rows: Row[] = [
    ...(hasCreate ? [{ kind: 'create' } as Row] : []),
    ...(clearLabel ? [{ kind: 'clear' } as Row] : []),
    ...filtered.map(e => ({ kind: 'entity', id: e.id }) as Row),
  ];
  const firstMatch = clearIdx + (clearLabel ? 1 : 0);

  useEffect(() => { if (hi >= 0) rowRefs.current[hi]?.scrollIntoView({ block: 'nearest' }); }, [hi]);

  function runRow(r: Row) {
    if (r.kind === 'clear') select('');
    else if (r.kind === 'entity') select(r.id);
    else { setFocused(false); setQuery(''); onCreateNew?.(); }
  }

  function select(id: string) {
    onChange(id);
    setFocused(false);
    setQuery('');
    inputRef.current?.blur();
  }

  /** Returns true when the key was used here (the grid shouldn't also act on it). */
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): boolean {
    if (e.key === 'Escape') {
      if (focused) e.stopPropagation();
      setFocused(false);
      setQuery('');
      setHi(-1);
      inputRef.current?.blur();
      return true;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (hi >= 0 && rows[hi]) runRow(rows[hi]);
      else if (filtered.length === 1) select(filtered[0].id);
      return true;
    }
    // While the list is open, Up / Down always walk it (grid cells too, e.g.
    // the Broker cells: asked for). Left / Right / Tab still leave the cell.
    if (focused && rows.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setHi(h => e.key === 'ArrowDown'
        ? Math.min(rows.length - 1, h < 0 ? firstMatch : h + 1)
        : Math.max(0, h - 1));
      return true;
    }
    return false;
  }

  const displayValue = focused ? query : (selected?.companyName ?? '');
  const showClear = !focused && !!selected && !disabled;

  return (
    <div style={{ position: 'relative', width: '100%', ...style }}>
      <input
        ref={el => { inputRef.current = el; cellRef?.(el); }}
        type="text"
        className={inputClassName}
        value={displayValue}
        placeholder={placeholder ?? 'Select…'}
        disabled={disabled}
        onFocus={() => { setFocused(true); setQuery(''); setHi(-1); }}
        onChange={e => {
          setQuery(e.target.value);
          // Typing highlights the first match, so Enter picks it.
          setHi(e.target.value.trim() ? firstMatch : -1);
        }}
        onKeyDown={e => { if (!handleKeyDown(e)) onKeyDownProp?.(e); }}
        autoComplete="off"
        style={showClear ? { paddingRight: 28 } : undefined}
      />
      {showClear && (
        <button
          type="button"
          aria-label="Clear selection"
          title="Clear"
          onClick={() => onChange('')}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            width: 16, height: 16, borderRadius: '50%', border: 'none',
            background: 'var(--border)', color: 'var(--text-2)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
          }}
        >
          {/* A drawn cross, not the "×" character: the font sat that glyph a
              little low in the circle, so it never looked centred. */}
          <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" style={{ display: 'block' }}>
            <path d="M1.5 1.5l5 5M6.5 1.5l-5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </button>
      )}
      {focused && pos && createPortal(
        <div
          ref={panelRef}
          className="dropdown-panel"
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
            maxHeight: 260, overflowY: 'auto',
          }}
        >
          {createLabel && onCreateNew && (
            <button
              type="button"
              ref={el => { rowRefs.current[0] = el; }}
              className={`dropdown-item${hi === 0 ? ' active' : ''}`}
              onMouseEnter={() => setHi(0)}
              style={{ color: 'var(--primary)', fontWeight: 600, borderBottom: '1px solid var(--border)', marginBottom: 2 }}
              onClick={() => { setFocused(false); setQuery(''); onCreateNew(); }}
            >
              {createLabel}
            </button>
          )}
          {clearLabel && (
            <button type="button" ref={el => { rowRefs.current[clearIdx] = el; }}
              className={`dropdown-item${(hi >= 0 ? hi === clearIdx : !value) ? ' active' : ''}`}
              onMouseEnter={() => setHi(clearIdx)} onClick={() => select('')}>
              {clearLabel}
            </button>
          )}
          {filtered.length === 0 && (
            <div className="dropdown-group-label" style={{ padding: '8px 9px' }}>No matches</div>
          )}
          {filtered.map((e, i) => (
            <button
              key={e.id}
              type="button"
              ref={el => { rowRefs.current[firstMatch + i] = el; }}
              className={`dropdown-item${(hi >= 0 ? hi === firstMatch + i : e.id === value) ? ' active' : ''}`}
              onMouseEnter={() => setHi(firstMatch + i)}
              onClick={() => select(e.id)}
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {e.companyName || <span style={{ opacity: 0.4 }}>—</span>}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
